import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Shimmer from '@nykaa/cart-shared/components/Shimmer';
import Disclaimer from '@nykaa/cart-shared/components/Disclaimer';
import ErrorScreen from '@nykaa/cart-shared/components/ErrorScreen';
import Coupon from '@nykaa/cart-shared/wrapper/Coupon';
import EmptyBag from '@nykaa/cart-shared/wrapper/EmptyBag';
import AuthRedirect from '@nykaa/cart-shared/wrapper/AuthRedirect';
import PaymentDetails from '@nykaa/cart-shared/wrapper/PaymentDetails';
import { ICartRefreshParams } from '@nykaa/cart-shared/store/types/requestParams';
import {
  CookieName,
  deviceTypes,
  ORIGIN_SOURCE,
} from '@nykaa/cart-shared/constants';
import { logger } from '@nykaa/logger';
import { getCookie } from '@nykaa/utils/cookies';
import Toast, { VARIANT } from '@nykaa/ui-components/Toast';
import { pushData } from '@nykaa/data-layer/utils';
import { cartRefresh } from '@nykaa/cart-shared/store/service';
import { CartRefreshType } from '@nykaa/cart-shared/store/types';
import { CartEvents } from '@nykaa/cart-shared/constants/tracking';
import { getAnalyticsData } from '@nykaa/cart-shared/helpers/tracking';
import { RewardsDTOType } from '@nykaa/cart-shared/store/types/payment';

import Header from './components/Header';
import Message from './components/Message';
import PickFreeProductCard from './components/PickFreeProductCard';
import RewardPoints from './components/RewardPoints';
import { ShoppingBagProps } from './types';
import { State } from 'store/types';
import {
  CartBodyLayout,
  Layout,
  MarginDiv,
  ScreenReaderLiveRegion,
} from './styled';
import { useIsLoggedIn } from 'hooks/auth';
import RestoreBag from './components/RestoreBag';
import MobFooter from './components/Footer';
import PickFreeSample from './components/PickFreeSample';
import DFAWidget from './components/DFA';
import EDD from './components/EDD';
import Product, { setItemQuantityInStorage } from './components/Products';
import FreeShippingWidget from './components/FreeShippingWidget';
import OOSAlertWidget from './components/OOSMessage';
import Prive from './components/Prive';
import { getEligibleForVariant } from 'helpers/experimentV2';
import {
  AB_CART_BEST_COUPON,
  AB_CART_DFA,
  DWEB_COLLAPSED_PRICE,
  AB_EMPTY_CART,
  AB_PRIVE_BAND,
} from 'constants/experiments';
import { pages } from 'constants/urls';
import { getPageSource } from 'helpers/shoppingBag';
import { useIsConvenienceFeeEnabled } from 'helpers/utils/hooks';
import { scrollToElement } from '@nykaa/cart-shared/helpers/utils';

const ShoppingBag = ({
  setCartInfo,
  setToastMessage,
  getFreeSamples,
  toggleCouponPage,
}: ShoppingBagProps) => {
  // eslint-disable-next-line no-console
  console.log('Migrated Shopping Bag');
  const [showPickFreeSampleScreen, setShowPickFreeSampleScreen] =
    useState(false);
  const [showRestoreBag, setShowRestoreBag] = useState(true);
  const [refreshError, setRefreshError] = useState(false);
  const [initialLoad, setInitialLoad] = useState(false);
  const [showOOSBottomSheet, setShowOOSBottomSheet] = useState(false);
  const collapsedPriceDetailsEnabled = useSelector((state: State) =>
    getEligibleForVariant(state, DWEB_COLLAPSED_PRICE, 'A')
  );
  const collapsedPriceDetails = useSelector(
    (state: State) => state.remoteConfig.configs.collapsedPriceDetails
  );
  const [announceText, setAnnounceText] = useState('');
  const [shouldExpandPriceDetails, setShouldExpandPriceDetails] =
    useState(false);
  const isLoggedIn = useIsLoggedIn();

  const { cartInfo, toastMessage } = useSelector(
    (state: State) => state.cartReducer
  );
  const userData = useSelector((state: State) => state.auth.user) || {};
  const isDFAEnabled =
    useSelector((state: State) =>
      getEligibleForVariant(state, AB_CART_DFA, 'A')
    ) || false;

  const bestCouponEnabled =
    useSelector((state: State) =>
      getEligibleForVariant(state, AB_CART_BEST_COUPON, 'A')
    ) || false;

  const priveWidgetEnabled =
    useSelector((state: State) =>
      getEligibleForVariant(state, AB_PRIVE_BAND, 'A')
    ) || false;
  const isConvenienceFeeEnabled = useIsConvenienceFeeEnabled();
  const isEmptyCartExpEnabled =
    useSelector((state: State) =>
      getEligibleForVariant(state, AB_EMPTY_CART, 'A')
    ) || false;
  const convenienceChargesEnabled = useSelector(
    (state: State) => state.remoteConfig.switches?.convenienceChargesEnabled
  );
  const {
    cartEddWidget,
    cartAvoidShipping,
    cartItemDiscount,
    cartMultiCouponEnabled,
  } = useSelector((state: State) => state.remoteConfig.switches);

  const {
    items,
    cartPageBottomCommunicationMessages,
    deltaCommunicationDTO,
    shippingDataDTO,
    cartPriceDTO,
    rewardsDTO,
  } = cartInfo || {};
  const { freeSamplesCommunicationDTO } = deltaCommunicationDTO || {};

  const { shippingChargeLimit } = shippingDataDTO || {};

  const showFreeShippingWidget =
    cartAvoidShipping &&
    (shippingChargeLimit || 0) > (cartPriceDTO?.bagTotal || 0);

  const loadCartInfo = async () => {
    setInitialLoad(true);
    const addressId =
      getCookie(CookieName.ADDRESS_ID_FOR_DELIVERY).toString() || '';
    const pincode =
      getCookie(CookieName.PIN_CODE_FOR_DELIVERY).toString() || '';
    const params: ICartRefreshParams = {
      deviceType: deviceTypes.DESKTOP,
      originSource: ORIGIN_SOURCE.CART,
      domain: __PLATFORM__,
      catalog_tag_filter: __PLATFORM__,
    };

    if (pincode && !addressId) {
      params.pincode = pincode;
    }

    try {
      const data: CartRefreshType = await cartRefresh(
        params,
        isConvenienceFeeEnabled
      );
      if (data) {
        setItemQuantityInStorage(data?.quantity);
        const itemsTrackingData = getAnalyticsData(CartEvents.DATA_LOAD, {
          ...data,
        });

        const trackingData = getAnalyticsData(CartEvents.DATA_LOAD_MIXPANEL, {
          ...data,
          userData,
          isOOSBottomsheetValid: showOOSBottomSheet,
          isAvoidShippingValid: showFreeShippingWidget,
          dfaVisible: isDFAEnabled,
          EDDSwitch: cartEddWidget,
          interactionLocation: getPageSource(),
          isBestCouponEnabled: bestCouponEnabled,
          bestCoupon: data?.bestCoupon,
          network_type: (navigator as any)?.connection?.effectiveType || '',
          enableItemDiscount: cartItemDiscount,
          isConvenienceFeeEnabled,
        });

        pushData(itemsTrackingData.gtmData);
        setTimeout(() => {
          pushData(trackingData.gtmData);
        }, 0);
        setCartInfo(data);
      }
    } catch (error) {
      setRefreshError(true);
      logger.error(error as Error, 'Error while fetching cart info');
    } finally {
      setInitialLoad(false);
    }
  };

  const loadFreeSamples = async () => {
    const params: ICartRefreshParams = {
      deviceType: deviceTypes.DESKTOP,
      originSource: ORIGIN_SOURCE.CART,
      domain: __PLATFORM__,
    };

    try {
      await getFreeSamples(params, isConvenienceFeeEnabled);
    } catch (error) {
      setToastMessage('Something went wrong!');
      logger.error(error as Error, 'Error while fetching free samples');
    }
  };

  useEffect(() => {
    setShowRestoreBag(true);
  }, [cartInfo.itemCount]);

  const togleShowRestoreBag = () => {
    setShowRestoreBag((status: boolean) => !status);
  };

  useEffect(() => {
    loadCartInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFreeSampleBack = () => {
    setShowPickFreeSampleScreen(false);
    loadCartInfo();
  };

  const pickFreeProductHandler = async () => {
    setShowPickFreeSampleScreen(true);
    loadFreeSamples();
  };

  const toggleShowOOSBottomSheet = () => {
    setShowOOSBottomSheet((status: boolean) => !status);
  };

  const handleCouponRedirection = () => {
    toggleCouponPage();
  };

  const getQuantAnnounceText = (text: string) => {
    setAnnounceText(text);
  };

  const grandTotalIconClickHandler = () => {
    const item = document.getElementById('payment_details');
    scrollToElement(item);
    setShouldExpandPriceDetails(true);
  };

  const loadCartBody = Object.keys(cartInfo).length > 0 && items.length > 0;

  // For loading State
  if (initialLoad) {
    return (
      <Layout onClick={(e) => e?.stopPropagation()}>
        <Header
          showPickFreeSampleScreen={showPickFreeSampleScreen}
          handleFreeSampleBack={handleFreeSampleBack}
        />
        <CartBodyLayout>
          <Shimmer />
        </CartBodyLayout>
      </Layout>
    );
  }

  // For Error State
  if (refreshError) {
    return (
      <Layout onClick={(e) => e?.stopPropagation()}>
        <Header
          showPickFreeSampleScreen={showPickFreeSampleScreen}
          handleFreeSampleBack={handleFreeSampleBack}
        />
        <ErrorScreen
          action={() => {
            window.location.reload();
          }}
        />
      </Layout>
    );
  }

  // For Empty Bag State
  if (cartInfo.quantity === 0) {
    return (
      <Layout onClick={(e) => e?.stopPropagation()}>
        <Header
          showPickFreeSampleScreen={showPickFreeSampleScreen}
          handleFreeSampleBack={handleFreeSampleBack}
        />
        <CartBodyLayout>
          {freeSamplesCommunicationDTO && showRestoreBag && (
            <RestoreBag
              freeSamplesCommunicationDTO={freeSamplesCommunicationDTO}
              togleShowRestoreBag={togleShowRestoreBag}
            />
          )}
          <EmptyBag
            experiments={{
              emptyCartExpEnabled: isEmptyCartExpEnabled,
            }}
            action={() => (window.location.href = pages.HOME)}
          />
        </CartBodyLayout>
      </Layout>
    );
  }

  if (showPickFreeSampleScreen) {
    return (
      <Layout onClick={(e) => e?.stopPropagation()}>
        <Header
          showPickFreeSampleScreen={showPickFreeSampleScreen}
          handleFreeSampleBack={handleFreeSampleBack}
        />
        <PickFreeSample
          togleShowPickFreeSamplesScreen={handleFreeSampleBack}
          isConvenienceFeeEnabled={isConvenienceFeeEnabled}
        />
      </Layout>
    );
  }

  return (
    <Layout onClick={(e) => e?.stopPropagation()}>
      <ScreenReaderLiveRegion
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {announceText}
      </ScreenReaderLiveRegion>
      <Header
        showPickFreeSampleScreen={showPickFreeSampleScreen}
        handleFreeSampleBack={handleFreeSampleBack}
      />
      <CartBodyLayout data-test-id="cart_body">
        {loadCartBody ? (
          <>
            <Message />
            <OOSAlertWidget
              showBottomSheet={showOOSBottomSheet}
              toggleShowOOSBottomSheet={toggleShowOOSBottomSheet}
              isConvenienceFeeEnabled={isConvenienceFeeEnabled}
            />
            {cartEddWidget && (
              <EDD isConvenienceFeeEnabled={isConvenienceFeeEnabled} />
            )}
            <PickFreeProductCard clickHandler={pickFreeProductHandler} />
            {!collapsedPriceDetailsEnabled && (
              <RewardPoints
                isConvenienceFeeEnabled={isConvenienceFeeEnabled}
                collapsedPriceDetailsEnabled={collapsedPriceDetailsEnabled}
                getQuantAnnounceText={getQuantAnnounceText}
              />
            )}
            {!isLoggedIn && (
              <AuthRedirect
                rewardsDTO={rewardsDTO || ({} as RewardsDTOType)}
                action={() => {
                  window.location.href = pages.AUTH_PAGE;
                }}
              />
            )}
            <Product
              isConvenienceFeeEnabled={isConvenienceFeeEnabled}
              getQuantAnnounceText={getQuantAnnounceText}
            />
            {freeSamplesCommunicationDTO && showRestoreBag && (
              <RestoreBag
                freeSamplesCommunicationDTO={freeSamplesCommunicationDTO}
                togleShowRestoreBag={togleShowRestoreBag}
                isConvenienceFeeEnabled={isConvenienceFeeEnabled}
              />
            )}
            <Coupon
              cartInfo={cartInfo}
              experiments={{ isMultiCouponsVisible: cartMultiCouponEnabled }}
              handleCouponRedirection={handleCouponRedirection}
            />
            {collapsedPriceDetailsEnabled && (
              <RewardPoints
                isConvenienceFeeEnabled={isConvenienceFeeEnabled}
                collapsedPriceDetailsEnabled={collapsedPriceDetailsEnabled}
                getQuantAnnounceText={getQuantAnnounceText}
              />
            )}
            <PaymentDetails
              cartInfo={cartInfo}
              experiments={{
                convenienceChargesEnabled: convenienceChargesEnabled,
                isConvenienceFeeEnabled: isConvenienceFeeEnabled,
                collapsedPriceDetailsEnabled: collapsedPriceDetailsEnabled,
              }}
              deviceType={deviceTypes.DESKTOP}
              collapsedPriceDetails={collapsedPriceDetails}
              shouldExpandPriceDetails={shouldExpandPriceDetails}
              setShouldExpandPriceDetails={setShouldExpandPriceDetails}
              ariaLevel={2}
            />
            {!!(isLoggedIn && priveWidgetEnabled) && <Prive />}
            {!!cartPageBottomCommunicationMessages?.length && (
              <Disclaimer messages={cartPageBottomCommunicationMessages} />
            )}
            <MarginDiv />
            {showFreeShippingWidget && (
              <FreeShippingWidget
                fetchCartInfo={loadCartInfo}
                isConvenienceFeeEnabled={isConvenienceFeeEnabled}
              />
            )}
            {isDFAEnabled && (
              <DFAWidget
                fetchCartInfo={loadCartInfo}
                isConvenienceFeeEnabled={isConvenienceFeeEnabled}
              />
            )}
          </>
        ) : (
          <Shimmer />
        )}
      </CartBodyLayout>
      {toastMessage && (
        <Toast
          variant={VARIANT.single}
          hideTimer
          message={toastMessage}
          timer={2}
          onDismiss={() => setToastMessage('')}
        />
      )}
      <MobFooter
        toggleShowOOSBottomSheet={toggleShowOOSBottomSheet}
        isConvenienceFeeEnabled={isConvenienceFeeEnabled}
        grandTotalIconClickHandler={grandTotalIconClickHandler}
      />
    </Layout>
  );
};

export default ShoppingBag;
